import { onTranslationsApplied, t } from './localization.ts';
import { onRedBannerEvent, onTopReturn, setNavigationBlocked, slideController } from './slides.ts';
import { addScrollListener, getScrollTop } from './scrollContainer.ts';
import { isIOS } from './ios-detector.ts';
import type { PopupReason, PopupSeenListener } from './types.ts';

let popup: HTMLElement | null = null;
let popupScaler: HTMLElement | null = null;
let popupDialog: HTMLElement | null = null;
let popupTitle: HTMLElement | null = null;
let popupDesc: HTMLElement | null = null;
let btnPrimary: HTMLButtonElement | null = null;
let btnGhost: HTMLButtonElement | null = null;

let popupOpen: boolean = false;
let focusRestoreEl: HTMLElement | null = null;
let redBannerPopupTimer: number | null = null;
let fastTopPopupTimer: number | null = null;
let exitIntentHandlerBound: boolean = false;
let scrollListenerBound: boolean = false;
let popupInitialized: boolean = false;

const seenReasons = new Set<PopupReason>();
const popupSeenSubscribers = new Set<PopupSeenListener>();

let lastScrollY: number = getScrollTop();
let lastScrollT: number = performance.now();

const POPUP_REASONS_LIST = ['fast-to-top', 'exit-intent', 'red-banner'];
const FAST_TOP_POPUP_DELAY_MS = 250;

function markSeen(reason: PopupReason): void {
  if (!reason) return;
  if (!seenReasons.has(reason)) {
    seenReasons.add(reason);
    notifyPopupSeen();
  }
}

function hasSeenAnyPopup(): boolean {
  return seenReasons.size > 0;
}

function onPopupSeen(fn: PopupSeenListener): () => void {
  if (typeof fn !== 'function') return () => {};
  popupSeenSubscribers.add(fn);
  return () => popupSeenSubscribers.delete(fn);
}

function notifyPopupSeen(): void {
  popupSeenSubscribers.forEach((fn) => {
    try {
      fn({ reasons: Array.from(seenReasons) });
    } catch (err) {
      console.error('Popup seen subscriber failed', err);
    }
  });
}

function clearRedBannerPopupTimer(): void {
  if (redBannerPopupTimer !== null) {
    clearTimeout(redBannerPopupTimer);
    redBannerPopupTimer = null;
  }
}

function clearFastTopPopupTimer(): void {
  if (fastTopPopupTimer !== null) {
    clearTimeout(fastTopPopupTimer);
    fastTopPopupTimer = null;
  }
}

function getSharedPopupCopy(): { title: string; desc: string; primary: string; secondary: string } {
  return {
    title: t('popups.shared.title', 'Why a Pop-Up?'),
    desc: t(
      'popups.shared.desc',
      'Last-ditch guilt trip. Clock runs out or you try to leave.<br>You hate it. It works anyway.'
    ),
    primary: t('popups.shared.primary', "I see what's going on!"),
    secondary: t('popups.shared.secondary', 'WTF is happening?'),
  };
}

function applyPopupCopy(
  copy: { title: string; desc: string; primary: string; secondary: string } | null
): void {
  if (!copy) return;
  if (popupTitle) popupTitle.textContent = copy.title;
  if (popupDesc) popupDesc.innerHTML = copy.desc;
  if (btnPrimary) btnPrimary.textContent = copy.primary;
  if (btnGhost) btnGhost.textContent = copy.secondary;
}

function localizePopupCopy(): void {
  if (!popupOpen) return;
  applyPopupCopy(getSharedPopupCopy());
}

function setPopupOriginFromRect(rect: DOMRect | null): void {
  if (!popupScaler) return;
  let originX = window.innerWidth / 2;
  let originY = window.innerHeight / 2;
  if (rect) {
    const left = Math.max(0, Math.min(window.innerWidth, rect.left));
    const top = Math.max(0, Math.min(window.innerHeight, rect.top));
    originX = left + Math.min(rect.width, window.innerWidth) / 2;
    originY = top + Math.min(rect.height, window.innerHeight) / 2;
  }
  popupScaler.style.setProperty('--origin-x', `${originX}px`);
  popupScaler.style.setProperty('--origin-y', `${originY}px`);
}

function preventTouchScroll(e: TouchEvent): void {
  e.preventDefault();
}

function hidePopup(): void {
  if (!popupOpen || !popup) return;
  popupOpen = false;
  popup.classList.remove('open');
  popup.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('modal-open');
  document.documentElement.classList.remove('modal-open');
  setNavigationBlocked(false);

  document.removeEventListener('keydown', onPopupKeydown);
  popup.removeEventListener('click', onBackdropClick);
  // Popup is closed via backdrop click, Escape key, or primary/ghost buttons

  // iOS: Remove touch scroll prevention
  if (isIOS()) {
    document.removeEventListener('touchmove', preventTouchScroll);
  }

  if (focusRestoreEl) {
    try {
      focusRestoreEl.focus();
    } catch (err) {
      console.error('Failed to restore focus after popup', err);
    }
    focusRestoreEl = null;
  }
}

function onPopupKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.preventDefault();
    hidePopup();
    return;
  }
  if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
    e.preventDefault();
    return;
  }
  if (e.key === 'Tab') {
    const focusables = popup!.querySelectorAll(
      'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
    );
    const list = Array.from(focusables).filter(
      (el) => !el.hasAttribute('disabled') && (el as HTMLElement).offsetParent !== null
    ) as HTMLElement[];
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      (last as HTMLElement).focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      (first as HTMLElement).focus();
    }
  }
}

function onBackdropClick(e: MouseEvent): void {
  if (e.target === popup) hidePopup();
}

function shouldAdvanceAfterPopup(): boolean {
  const controller = slideController;
  if (!controller) return false;
  const index = controller.index ?? 0;
  // Only advance on the first five slides (indices 0-4, including #slow-down at 3)
  return index >= 0 && index <= 4;
}

function onPopupPrimaryClick(): void {
  if (!popupOpen) return;
  const shouldAdvance = shouldAdvanceAfterPopup();

  if (isIOS()) {
    // iOS Safari: Close popup FIRST, then scroll
    // Modal state blocks programmatic scrolling on iOS
    hidePopup();
    if (shouldAdvance) {
      // Small delay to let modal state clear
      setTimeout(() => {
        slideController?.goNext?.({ source: 'popup-primary' });
      }, 50);
    }
  } else {
    // Other browsers: Scroll first, then close popup
    if (shouldAdvance) {
      // Temporarily unblock navigation so goNext can run while modal is open
      setNavigationBlocked(false);
      slideController?.goNext?.({ source: 'popup-primary' });
      requestAnimationFrame(() => {
        setTimeout(() => hidePopup(), 150);
      });
    } else {
      hidePopup();
    }
  }
}

function onPopupSecondaryClick(): void {
  if (!popupOpen) return;
  const shouldAdvance = shouldAdvanceAfterPopup();

  if (isIOS()) {
    // iOS Safari: Close popup FIRST, then scroll
    // Modal state blocks programmatic scrolling on iOS
    hidePopup();
    if (shouldAdvance) {
      // Small delay to let modal state clear
      setTimeout(() => {
        slideController?.goNext?.({ source: 'popup-secondary' });
      }, 50);
    }
  } else {
    // Other browsers: Scroll first, then close popup
    if (shouldAdvance) {
      // Temporarily unblock navigation so goNext can run while modal is open
      setNavigationBlocked(false);
      slideController?.goNext?.({ source: 'popup-secondary' });
      requestAnimationFrame(() => {
        setTimeout(() => hidePopup(), 150);
      });
    } else {
      hidePopup();
    }
  }
}

function showPopup(reason: PopupReason, originRect: DOMRect | null = null): void {
  if (!popup || popupOpen) return;
  if (hasSeenAnyPopup()) return;

  clearRedBannerPopupTimer();
  clearFastTopPopupTimer();
  markSeen(reason || 'unknown');

  const copy = getSharedPopupCopy();
  applyPopupCopy(copy);

  setPopupOriginFromRect(originRect);

  popup.setAttribute('aria-hidden', 'false');
  popup.classList.add('open');
  document.body.classList.add('modal-open');
  document.documentElement.classList.add('modal-open');
  setNavigationBlocked(true);
  popupOpen = true;

  focusRestoreEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  popupDialog?.focus();

  document.addEventListener('keydown', onPopupKeydown);
  popup.addEventListener('click', onBackdropClick);
  // Close handled by backdrop click (onBackdropClick) and Escape key (onPopupKeydown)

  // iOS: Prevent background scrolling with touch events
  if (isIOS()) {
    document.addEventListener('touchmove', preventTouchScroll, { passive: false });
  }
}

function scheduleRedBannerPopup(rectProvider?: () => DOMRect | null): void {
  if (hasSeenAnyPopup() || popupOpen) return;
  clearRedBannerPopupTimer();
  redBannerPopupTimer = window.setTimeout(() => {
    redBannerPopupTimer = null;
    if (hasSeenAnyPopup() || popupOpen) return;
    const rect = rectProvider?.() || null;
    showPopup('red-banner', rect);
  }, 1300);
}

function scheduleFastTopPopup(originRect: DOMRect | null = null): void {
  if (hasSeenAnyPopup() || popupOpen) return;
  if (fastTopPopupTimer !== null) return;
  fastTopPopupTimer = window.setTimeout(() => {
    fastTopPopupTimer = null;
    if (hasSeenAnyPopup() || popupOpen) return;
    showPopup('fast-to-top', originRect);
  }, FAST_TOP_POPUP_DELAY_MS);
}

function onFastTopScroll(): void {
  if (hasSeenAnyPopup() || popupOpen) return;

  const now = performance.now();
  const y = getScrollTop();
  const dy = lastScrollY - y;
  const dt = Math.max(1, now - lastScrollT);
  const velocity = (dy / dt) * 1000;
  const distanceQuickReturn = dy > 400;
  const timeQuickReturn = dt < 800;

  if (y < 200 && (velocity > 1200 || (distanceQuickReturn && timeQuickReturn))) {
    scheduleFastTopPopup(null);
  } else if (y >= 200) {
    clearFastTopPopupTimer();
  }

  lastScrollY = y;
  lastScrollT = now;
}

function onMouseOut(e: MouseEvent): void {
  if (hasSeenAnyPopup() || popupOpen) return;
  if (!e.relatedTarget && e.clientY <= 0) {
    showPopup('exit-intent', null);
    document.removeEventListener('mouseout', onMouseOut);
    exitIntentHandlerBound = false;
  }
}

function bindExitIntent(): void {
  if (exitIntentHandlerBound) return;
  document.addEventListener('mouseout', onMouseOut);
  exitIntentHandlerBound = true;
}

function bindScrollListener(): void {
  if (scrollListenerBound) return;
  const detach = addScrollListener(() => onFastTopScroll());
  scrollListenerBound = true;
  const originalRemove = () => detach();
  // store removal for future (overriding function scope variable)
  (bindScrollListener as unknown as { detach?: () => void }).detach = originalRemove;
}

function handleTopReturn(): void {
  if (hasSeenAnyPopup() || popupOpen) return;
  scheduleFastTopPopup(null);
}

function initPopup(): void {
  if (popupInitialized) {
    return;
  }

  const root = document.getElementById('popup');
  if (!root) {
    window.requestAnimationFrame(initPopup);
    return;
  }

  popup = root;
  popupScaler = popup.querySelector('.popup-scaler');
  popupDialog = popup.querySelector('.popup-dialog');
  popupTitle = popup.querySelector('#popupTitle');
  popupDesc = popup.querySelector('#popupDesc');
  btnPrimary = popup.querySelector('.btn-primary');
  btnGhost = popup.querySelector('.btn-ghost');

  // Add both click and touch handlers for better iOS responsiveness
  btnPrimary?.addEventListener('click', onPopupPrimaryClick);
  btnGhost?.addEventListener('click', onPopupSecondaryClick);

  // iOS: Add touchend to prevent 300ms click delay
  if (isIOS()) {
    btnPrimary?.addEventListener(
      'touchend',
      (e) => {
        e.preventDefault();
        onPopupPrimaryClick();
      },
      { passive: false }
    );

    btnGhost?.addEventListener(
      'touchend',
      (e) => {
        e.preventDefault();
        onPopupSecondaryClick();
      },
      { passive: false }
    );
  }

  bindExitIntent();
  bindScrollListener();

  onTopReturn(handleTopReturn);
  onRedBannerEvent((event: { type: string; getRect?: () => DOMRect | null; visible?: boolean }) => {
    if (event?.type === 'at-slide') {
      scheduleRedBannerPopup(event.getRect);
    } else if (event?.type === 'away' || event?.type === 'hide') {
      clearRedBannerPopupTimer();
    }
  });

  onTranslationsApplied(localizePopupCopy);
  popupInitialized = true;
}

function getPopupReasons(): string[] {
  return POPUP_REASONS_LIST.slice();
}

export { getPopupReasons, hasSeenAnyPopup, hidePopup, initPopup, onPopupSeen, showPopup };
